import React, { useMemo } from "react";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import {
    orderPaymentDetailsTableColumns,
    voucherDetailsTableColumns,
} from "../../../../config/roles";
import { PAYMENT_TYPES, USER_ROLES, WALLET_TYPES } from "../../../../config/constants";
import translator from "../../../../components/localization/translator";
import security from "../../../../components/common/security/security";
import TipTransactionsTable from "./TipTransactionsTable";
import PosTransactionsTable from "./PosTransactionsTable";

const centerColumnStyle = {
    display: "flex",
    justifyContent: "center",
    padding: "8px",
    margin: "0 -8px",
};

const columnWrapperStyle = {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
};

const getNestedValue = (row, path) =>
    path.split(".").reduce((acc, key) => acc?.[key], row) ?? "";

const renderPaymentType = (_, row) => {
    const {
        paymentType,
        walletType,
        usedBonusPoints,
        usedGiftCard,
        invoiceAmount,
    } = row;

    const paymentTypeName =
        PAYMENT_TYPES.paymentMethodsById(paymentType)?.name || "";

    const bonusType =
        usedBonusPoints > 0
            ? {
                [WALLET_TYPES.BONUS]: PAYMENT_TYPES.BONUS.name,
                [WALLET_TYPES.CORPORATE_BONUS]:
                PAYMENT_TYPES.CORPORATE_BONUS.name,
            }[walletType]
            : null;

    const rows = [
        invoiceAmount && paymentTypeName,
        usedBonusPoints && bonusType,
        usedGiftCard && translator.translate("_gift_card_"),
    ].filter(Boolean);

    if (!rows.length) return "";

    return (
        <div style={columnWrapperStyle}>
            {rows.map((item, index) => (
                <div key={index} style={centerColumnStyle}>
                    {item}
                </div>
            ))}
        </div>
    );
};

const renderPaymentInvoice = (_, row) => {
    const { usedBonusPoints, usedGiftCard, invoiceAmount } = row;

    const rows = [
        invoiceAmount,
        usedBonusPoints,
        usedGiftCard,
    ].filter(Boolean);

    if (!rows.length) return "";

    return (
        <div style={columnWrapperStyle}>
            {rows.map((item, index) => (
                <div key={index} style={centerColumnStyle}>
                    {item}
                </div>
            ))}
        </div>
    );
};

const renderUserName = (cell, row, paymentDetails) => {
    const isInitiator =
        paymentDetails?.[0]?.userId &&
        row.userId === paymentDetails[0].userId;

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <span>{cell}</span>
            {isInitiator && <span>(նախաձեռնող)</span>}
        </div>
    );
};

const buildColumns = (columnsConfig, paymentDetails) =>
    columnsConfig.map((column, index) => {
        let formatter = (cell) => cell ?? "";

        if (column.isNested) {
            formatter = (_, row) => getNestedValue(row, column.key);
        }

        if (column.key === "paymentType") {
            formatter = renderPaymentType;
        }

        if (column.key === "invoiceAmount") {
            formatter = renderPaymentInvoice;
        }

        if (column.key === "userName") {
            formatter = (cell, row) =>
                renderUserName(cell, row, paymentDetails);
        }

        return (
            <TableHeaderColumn
                key={index}
                expandable={false}
                isKey={column.isKey}
                dataField={column.key}
                dataSort={column.dataSort}
                dataFormat={formatter}
                width={column.width}
            >
                {translator.translate(column.title)}
            </TableHeaderColumn>
        );
    });

const Section = ({ title, children }) => (
    <div className="table-section">
        {title && <h3 className="table-section__title">{title}</h3>}
        {children}
    </div>
);

const DataTable = ({ data, columns, emptyMessage }) => {
    if (!data?.length) {
        return emptyMessage ? <div>{emptyMessage}</div> : null;
    }

    return (
        <BootstrapTable data={data} striped hover>
            {columns}
        </BootstrapTable>
    );
};

export function PaymentDetails({
                                   paymentDetails = [],
                                   voucherDetails,
                                   posTransactions = [],
                                   tipTransactions = {},
                                   orderDetails = {},
                               }) {

    const roleId = security.getUser().role;
    const isAdmin = USER_ROLES.SUPER_ADMIN === roleId;

    const tableColumns = useMemo(
        () => buildColumns(orderPaymentDetailsTableColumns, paymentDetails),
        [paymentDetails],
    );

    const voucherColumns = useMemo(
        () => buildColumns(voucherDetailsTableColumns, paymentDetails),
        [paymentDetails],
    );

    const voucherTableData = [
        ...paymentDetails
            .filter((p) => p.discount)
            .map((p) => p.discount),
        ...(voucherDetails ? [voucherDetails] : []),
    ];

    return (
        <div className="payment-tables-wrapper">
            <DataTable
                data={paymentDetails}
                columns={tableColumns}
            />

            <Section title={translator.translate("_voucher_details_")}>
                <DataTable
                    data={voucherTableData}
                    columns={voucherColumns}
                    emptyMessage={translator.translate(
                        "_voucher_transactions_not_found_",
                    )}
                />
            </Section>

            <hr />
            <Section title={translator.translate("_pos_payment_details_")}>
                <PosTransactionsTable data={posTransactions} />
            </Section>

            <hr />
            {isAdmin && orderDetails?.status === 4 &&
                <Section title={translator.translate("_tip_")}>
                    <TipTransactionsTable data={Object.keys(tipTransactions)?.length ? [tipTransactions] : []} />
                </Section>
            }
        </div>
    );
}
